import validateInitialization from "../../../core/js/helpers/validate-initialization";
import {
    componentName,
    defaultProps,
    views,
    componentClasses,
} from "../../../core/components/file-input/file-input";
import FileInputList from "./file-input-list";
import { fileInputListTemplate } from "./file-input-list.template";
import { createStore, renderState } from "../../../core/js/state-machine";
import { fileInputReducer } from "../../../core/js/state-machine/reducers";
import validateRequiredElements from "../../../core/js/helpers/validate-required-elements";

/**
 * Validate HTML
 * @param {HTMLElement}
 * @returns {Boolean}
 */
const validateHTML = (element) => {
    const requiredElements = {
        button: {
            query: `.${componentClasses.button}`,
            selector: element.querySelector(`.${componentClasses.button}`),
        },
        control: {
            query: `.${componentClasses.control}`,
            selector: element.querySelector(`.${componentClasses.control}`),
        },
        errorText: {
            query: `.${componentClasses.errorText}`,
            selector: element.querySelector(`.${componentClasses.errorText}`),
        },
    };
    const [success, elements] = validateRequiredElements({
        requiredElements,
        componentName,
    });

    return success ? elements : false;
};

/**
 * Initialize the File Input component
 * @param {HTMLElement} element main File Input wrapper
 * @param {defaultProps} props optional settings.
 * @returns {{
 *  element: HTMLElement,
 *  dismiss: Function,
 *  dispose: Function
 * }} public api object
 */
const FileInput = (element, props = {}) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        componentClasses,
        element,
        defaultProps,
        props,
        validateHTML,
        views,
    });
    if (!success) return;

    /**
     * Build our fileInputListElement so that we can use it.
     * @returns {HTMLElement} fileInputListElement
     */
    const buildFileInputListElement = () => {
        const fileInputListElement = new DOMParser().parseFromString(
            fileInputListTemplate(),
            "text/html"
        ).body.firstChild;

        element.insertAdjacentElement("beforeend", fileInputListElement);
        return fileInputListElement;
    };

    /**
     * When our input[type="file"] changes, we want to react to it.
     * @param {Event} event
     */
    const handleChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        selectedFiles.forEach((file) => fileInputList.add(file));
    };

    /**
     * When our list changes we want to update our control to have the latest FileList because the
     * input[type="file"] uses the FileList object and not a normal value.
     */
    const updateControlValue = () => {
        const { list } = fileInputStore.getState();
        const validItems = list.filter((item) => !item.error);
        // Use a dataTransfer class so we can add to a FileList (readonly) indirectly by using the
        // dataTransfer class and items and then attaching the dataTransfer files which only takes files from
        // the items **magic**.
        const dataTransfer = new DataTransfer();
        validItems.forEach((item) => dataTransfer.items.add(item.file));
        validElements.control.files = dataTransfer.files;
    };

    /**
     * When the file input has errors, it is "invalid", but we do not use the setCustomValidity
     * because the users can still submit the form even though the component is in an error state.
     * It will only send those files who are NOT in an error state.
     */
    const checkValidity = () => {
        const { validItemCount, list } = fileInputStore.getState();

        // Show the error-text if we have any errors.
        validItemCount === list.length
            ? renderState(views.valid, element)
            : renderState(views.invalid, element);
    };

    const updateButtonState = () => {
        const { validItemCount } = fileInputStore.getState();

        validItemCount >= mergedProps.maxFileCount
            ? renderState(views.button.inactive, validElements.button)
            : renderState(views.button.rest, validElements.button);
    };

    /**
     * When our state list of items changes, we want to update our component.
     */
    const handleStateChange = () => {
        updateControlValue();
        updateButtonState();
        checkValidity();
    };

    /**
     * When the button is clicked, we want to trigger the input so that we get the browser to open
     * the window explorer.
     */
    const handleButtonClick = (event) => {
        event.preventDefault();
        validElements.control.click();
    };

    /**
     * Dispose
     */
    const dispose = () => {
        validElements.control.removeEventListener("change", handleChange);
        validElements.button.removeEventListener("click", handleButtonClick);
    };

    // INIT

    const fileInputStore = createStore(fileInputReducer, {
        fieldError: false,
        list: [],
        fullItemCount: 0,
        validItemCount: 0,
    });

    fileInputStore.subscribe(handleStateChange);

    const fileInputListElement = buildFileInputListElement();

    const fileInputList = FileInputList(fileInputListElement, {
        ...mergedProps,

        fileInputStore,
    });

    // Listeners
    validElements.control.addEventListener("change", handleChange);
    validElements.button.addEventListener("click", handleButtonClick);

    /**
     * Public API
     */
    const api = {
        element,
        dispose,
        // List functions
        add: fileInputList.add,
        remove: fileInputList.remove,
        setState: fileInputList.setState,
    };

    element[componentName] = api;

    return api;
};

export default FileInput;
